/**
 *
 * @module cafPlayerAdapter
 * @author Shane Daugherty [shane@blackpixel.com]
 * @author Greg Babula [greg.babula@disneystreaming.com]
 * @author Eric Milliren [eric.milliren@disneystreaming.com]
 * @see https://developers.google.com/cast/docs/caf_receiver_overview
 * @see https://developers.google.com/cast/docs/reference/caf_receiver/cast.framework.PlayerManager
 * @see https://developers.google.com/cast/docs/reference/caf_receiver/cast.framework.PlaybackConfig
 * @see https://developers.google.com/cast/docs/reference/caf_receiver/cast.framework.messages.LoadRequestData
 * @see https://developers.google.com/cast/docs/reference/caf_receiver/cast.framework.messages.MediaInformation
 * @see https://developers.google.com/cast/docs/reference/caf_receiver/cast.framework.events#.EventType
 *
 */

import Check from 'check-types';
import Services from './../../services/entry';
import Drm from './../../drm/entry';
import PlayerAdapter from './../playerAdapter';
import PlaybackMetrics from './../playbackMetrics';
import PlaybackEventListener from './../playbackEventListener';

const Util = Services.Util;
const DrmProvider = Drm.DrmProvider;
const DrmType = Services.Media.DrmType;

const ErrorReason = Services.Exception.ErrorReason;
const ExceptionReference = Services.Exception.ExceptionReference;
const ServiceException = Services.Exception.ServiceException;
const InvalidArgumentException = Services.Exception.InvalidArgumentException;

const {
    assigned,
    instanceStrict,
    nonEmptyString
} = Check.assert;

/**
 *
 * @class CafPlayerAdapter
 * @extends {PlayerAdapter}
 * @since 3.1.0
 * @desc Chromecast Application Framework (CAF) PlayerAdapter.
 *
 */
export default class CafPlayerAdapter extends PlayerAdapter {

    /**
     *
     * @param {cast.framework.PlayerManager} nativePlayer
     * @param {cast.framework} framework
     * @throws {InvalidArgumentException}
     *
     */
    constructor(nativePlayer, framework) {

        const errorMessage = {
            ctor: 'CafPlayerAdapter(nativePlayer, framework)',
            get nativePlayer() {
                return `${this.ctor} needs cast.framework.PlayerManager instance`;
            },
            get nativePlayerInstance() {
                return `${this.ctor} native player should be a cast.framework.PlayerManager instance`;
            }
        };

        super(nativePlayer);

        Check.all([
            assigned(nativePlayer, errorMessage.nativePlayer, InvalidArgumentException),
            instanceStrict(nativePlayer, framework.PlayerManager, errorMessage.nativePlayerInstance, InvalidArgumentException) // eslint-disable-line
        ]);

        /**
         *
         * @access private
         * @type {cast.framework|null}
         * @desc Internal reference to the cast framework Object. Allows property
         * reference instead of checking against a cast global, which in turn makes this
         * PlayerAdapter easily testable.
         *
         */
        this.framework = framework;

        /**
         *
         * @access private
         * @type {Number}
         * @desc The current bit rate.
         * @todo see if there is a way to avoid storing this value
         * if we must do this, make sure that the value gets updated "onEnded" "onPause" etc
         *
         */
        this.currentBitrate = undefined;

        /**
         *
         * @access public
         * @since 3.2.0
         * @type {cast.framework.messages.HlsSegmentFormat}
         *
         */
        this.audioSegmentFormat = undefined;

        /**
         *
         * @access public
         * @since 3.2.0
         * @type {cast.framework.messages.HlsVideoSegmentFormat}
         *
         */
        this.videoSegmentFormat = null;

    }

    /**
     *
     * @access public
     * @param {String} playlistUri - The URL to be used during playback.
     * @desc Callback used when prepare has been called (usually via the {PlaybackSession}).
     * Sets the source URI on the {NativePlayer} instance.
     * @throws {InvalidStateException} Unable to set playlistUri on NativePlayer
     * @returns {Promise<Void>}
     *
     */
    setSource(playlistUri) {

        const errorMessage = {
            method: 'CafPlayerAdapter.setSource(playlistUri)',
            get playlistUri() {
                return `${this.method} uri should be a String with length`;
            },
            get setPlaylistUri() {
                return `${this.method} unable to set playlistUri on NativePlayer`;
            }
        };

        nonEmptyString(playlistUri, errorMessage.playlistUri, InvalidArgumentException);

        const { nativePlayer } = this;

        return new Promise((resolve, reject) => {

            this.playlistUri = playlistUri;

            try {

                /**
                 *
                 * @type {cast.framework.PlaybackConfig}
                 * @desc A copy of the current player config
                 *
                 */
                const playbackConfig = this.getPlaybackConfig();

                // undocumented Google MPL flag
                if (!Util.getSafe(() => playbackConfig.disableSourceBufferTimeAdjust)) {
                    playbackConfig.disableSourceBufferTimeAdjust = true;
                }

                // set current player config
                nativePlayer.setPlaybackConfig(playbackConfig);

                // override the mediaUrl / contentId in the LOAD request
                nativePlayer.setMediaUrlResolver(() => playlistUri);

                return resolve();

            } catch (ex) {

                const reasons = [new ErrorReason('', `${errorMessage.setPlaylistUri} - ${ex}`)];
                const exceptionData = ExceptionReference.common.invalidState;

                return reject(new ServiceException({ reasons, exceptionData }));

            }

        });

    }

    /**
     *
     * @access private
     * @since 3.6.0
     * @param {Object} adEngineData
     * @desc assigns adEngine data from the playlist service, some of which should be included in segment requests
     * @note if adEngineData is unassigned do not do anything
     *
     */
    setAdEngineData(adEngineData) {

        this.adEngineData = adEngineData;

        if (Check.nonEmptyObject(adEngineData)) {

            const { nativePlayer } = this;
            const { ssess } = adEngineData;

            const playbackConfig = this.getPlaybackConfig();

            playbackConfig.segmentRequestHandler = (requestInfo) => {

                const isAdEngineCall = Util.adEngineRegex(requestInfo.url);

                // need to reset these for each segment request
                requestInfo.headers = null;
                requestInfo.crossDomain = undefined;
                requestInfo.withCredentials = false;

                if (ssess && isAdEngineCall) {

                    requestInfo.headers = {};
                    requestInfo.headers.ssess = ssess;
                    requestInfo.crossDomain = true;
                    requestInfo.withCredentials = true;

                }

            };

            nativePlayer.setPlaybackConfig(playbackConfig);

        }

    }

    /**
     *
     * @access public
     * @desc Gets a snapshot of information about media playback.
     * @returns {Promise<PlaybackMetrics>} - instance that contains a snapshot
     * of information about media playback.
     *
     */
    getPlaybackMetrics() {

        return Promise.resolve(
            new PlaybackMetrics(
                this.getCurrentBitrate(),
                this.getCurrentPlayhead()
            )
        );

    }

    /**
     *
     * @access public
     * @param {PlaybackEventListener} listener
     * @throws {InvalidStateException} Unable to add PlaybackEventListener
     * @returns {Promise<Void>}
     *
     */
    addListener(listener) {

        const errorMessage = {
            method: 'CafPlayerAdapter.addListener(listener)',
            get listener() {
                return `${this.method} needs PlaybackEventListener instance`;
            },
            get addListener() {
                return `${this.method} unable to add PlaybackEventListener`;
            }
        };

        instanceStrict(listener, PlaybackEventListener, errorMessage.listener, InvalidArgumentException);

        const { nativePlayer, boundHandlers, framework } = this;
        const { onPlay, onPause, onError, onPlayedToCompletion } = this;
        const { PLAY, PAUSE, ERROR, MEDIA_FINISHED, BITRATE_CHANGED } = framework.events.EventType;

        return new Promise((resolve, reject) => {

            this.listener = listener;

            boundHandlers.onPlay = onPlay.bind(this);
            boundHandlers.onPause = onPause.bind(this);
            boundHandlers.onError = onError.bind(this);
            boundHandlers.onPlayedToCompletion = onPlayedToCompletion.bind(this);
            boundHandlers.setCurrentBitrate = this.setCurrentBitrate.bind(this);

            try {

                nativePlayer.addEventListener(
                    PLAY,
                    boundHandlers.onPlay
                );

                nativePlayer.addEventListener(
                    PAUSE,
                    boundHandlers.onPause
                );

                nativePlayer.addEventListener(
                    ERROR,
                    boundHandlers.onError
                );

                nativePlayer.addEventListener(
                    MEDIA_FINISHED,
                    boundHandlers.onPlayedToCompletion
                );

                nativePlayer.addEventListener(
                    BITRATE_CHANGED,
                    boundHandlers.setCurrentBitrate
                );

                return resolve();

            } catch (ex) {

                const reasons = [new ErrorReason('', errorMessage.addListener)];
                const exceptionData = ExceptionReference.common.invalidState;

                return reject(new ServiceException({ reasons, exceptionData }));

            }

        });

    }

    /**
     *
     * @access public
     * @param {PlaybackEventListener} listener
     * @throws {InvalidStateException} Unable to remove PlaybackEventListener
     * @returns {Promise<Void>}
     *
     */
    removeListener(listener) {

        const errorMessage = {
            method: 'CafPlayerAdapter.removeListener(listener)',
            get listener() {
                return `${this.method} needs PlaybackEventListener instance`;
            },
            get removeListener() {
                return `${this.method} unable to remove PlaybackEventListener`;
            }
        };

        instanceStrict(listener, PlaybackEventListener, errorMessage.listener, InvalidArgumentException);

        const { nativePlayer, boundHandlers, framework } = this;
        const { PLAY, PAUSE, ERROR, MEDIA_FINISHED, BITRATE_CHANGED } = framework.events.EventType;

        return new Promise((resolve, reject) => {

            if (Check.equal(this.listener, listener)) {

                this.listener = null;

                try {

                    nativePlayer.removeEventListener(
                        PLAY,
                        boundHandlers.onPlay
                    );

                    nativePlayer.removeEventListener(
                        PAUSE,
                        boundHandlers.onPause
                    );

                    nativePlayer.removeEventListener(
                        ERROR,
                        boundHandlers.onError
                    );

                    nativePlayer.removeEventListener(
                        MEDIA_FINISHED,
                        boundHandlers.onPlayedToCompletion
                    );

                    nativePlayer.removeEventListener(
                        BITRATE_CHANGED,
                        boundHandlers.setCurrentBitrate
                    );

                } catch (ex) {

                    const reasons = [new ErrorReason('', errorMessage.removeListener)];
                    const exceptionData = ExceptionReference.common.invalidState;

                    return reject(new ServiceException({ reasons, exceptionData }));

                }

            }

            return resolve();

        });

    }

    /**
     *
     * @access public
     * @since 3.2.0
     * @param {SDK.Drm.DrmProvider} drmProvider
     * @returns {Promise<Void>}
     *
     */
    setDrmProvider(drmProvider) {

        const errorMessage = {
            method: 'CafPlayerAdapter.setDrmProvider(drmProvider)',
            get drmProvider() {
                return `${this.method} needs SDK.Drm.DrmProvider instance`;
            }
        };

        instanceStrict(drmProvider, DrmProvider, errorMessage.drmProvider, InvalidArgumentException);

        this.drmProvider = drmProvider;

        return this.attach();

    }

    /**
     *
     * @access public
     * @param {Array<String>} audioSegmentTypes
     * @param {Array<String>} videoSegmentTypes
     * @since 3.2.0
     * @returns {Promise<Void>}
     *
     */
    updateSegmentFormat(audioSegmentTypes, videoSegmentTypes) {

        this.updateAudioSegmentFormat(audioSegmentTypes).updateVideoSegmentFormat(videoSegmentTypes);

        return Promise.resolve();

    }

    /**
     *
     * @access public
     * @desc Resets player adapter state. Removes all playback listeners.
     * @returns {Promise<Void>}
     *
     */
    clean() {

        const { listener, nativePlayer } = this;

        // clear the override the mediaUrl / contentId in the LOAD request
        nativePlayer.setMediaUrlResolver();

        this.playlistUri = '';
        this.boundHandlers = {};
        this.currentBitrate = null;
        this.drmProvider = null;
        this.audioSegmentFormat = undefined;
        this.videoSegmentFormat = undefined;

        if (Check.assigned(listener)) {
            return this.removeListener(listener);
        }

        return Promise.resolve();

    }

    /* #region private: */

    /**
     *
     * @access public
     * @since 3.1.0
     * @desc Completes the cleanup process by completely cleaning up all `PlayerAdapter`
     * references. This method should be executed by the application developer
     * when they no longer need to use a `PlayerAdapter` instance.
     * @note Because `clean` is automatically called by the `PlaybackSession` instance,
     * another method is necessary to handle the use case of an application developer who
     * wants to continue using a `PlayerAdapter` instance after a `PlaybackSession` has been released.
     * @returns {Promise<Void>}
     *
     */
    dispose() {

        this.nativePlayer = null;
        this.accessToken = null;
        this.framework = null;

        return Promise.resolve();

    }

    /**
     *
     * @access public
     * @param {cast.framework.events.BitrateChangedEvent} event
     * @desc Attempts to update the currentBitrate value
     * @returns {Void}
     *
     */
    setCurrentBitrate(event) {

        try {
            this.currentBitrate = Math.round(event.totalBitrate / 1000);
        } catch (ex) {
            this.currentBitrate = null;
        }

    }

    /**
     *
     * @access public
     * @desc Gets the current bitrate
     * @returns {Number|null}
     *
     */
    getCurrentBitrate() {

        const { unavailableMetric } = this;

        return Check.number(this.currentBitrate) ? this.currentBitrate : unavailableMetric;

    }

    /**
     *
     * @access public
     * @desc Gets the current playhead position
     * @returns {CafPlayerAdapter.unavailableMetric}
     *
     */
    getCurrentPlayhead() {

        const { nativePlayer, unavailableMetric } = this;
        const currentTimeSec = nativePlayer.getCurrentTimeSec();
        const playheadPosition = Check.number(currentTimeSec) ? currentTimeSec : unavailableMetric;

        return playheadPosition || unavailableMetric;

    }

    /**
     *
     * @access private
     * @since 3.2.0
     * @returns {Promise<Void>}
     *
     */
    attach() {

        const { drmProvider, framework } = this;

        if (Check.equal(drmProvider.type, DrmType.WIDEVINE)) {
            return this.attachDrm(Util.getSafe(() => framework.ContentProtection.WIDEVINE));
        }

        if (Check.equal(drmProvider.type, DrmType.PLAYREADY)) {
            return this.attachDrm(Util.getSafe(() => framework.ContentProtection.PLAYREADY));
        }

        return this.attachSilkDrm();

    }

    /**
     *
     * @access private
     * @since 3.2.0
     * @param {cast.framework.ContentProtection} protectionSystem
     * @throws {InvalidStateException} Unable to set playback configuration on NativePlayer
     * @returns {Promise<Void>}
     *
     */
    attachDrm(protectionSystem) {

        const errorMessage = {
            method: 'CafPlayerAdapter.attachDrm(protectionSystem)',
            get playbackConfiguration() {
                return `${this.method} unable to set playback configuration on NativePlayer`;
            }
        };

        const { nativePlayer, drmProvider } = this;

        try {

            /**
             *
             * @type {cast.framework.PlaybackConfig}
             * @desc A copy of the current player config
             *
             */
            const playbackConfig = this.getPlaybackConfig();

            playbackConfig.licenseUrl = drmProvider.licenseRequestUri;
            playbackConfig.protectionSystem = protectionSystem;

            // override license request handler to set credentials
            playbackConfig.licenseRequestHandler = (requestInfo) => {

                requestInfo.crossDomain = true;
                requestInfo.withCredentials = true;
                requestInfo.headers = drmProvider.processLicenseRequestHeaders() || {};

            };

            // set current player config
            nativePlayer.setPlaybackConfig(playbackConfig);

            return Promise.resolve();

        } catch (ex) {

            const reasons = [new ErrorReason('', `${errorMessage.playbackConfiguration} - ${ex}`)];
            const exceptionData = ExceptionReference.common.invalidState;

            return Promise.reject(new ServiceException({ reasons, exceptionData }));

        }

    }

    /**
     *
     * @access private
     * @since 3.2.0
     * @throws {InvalidStateException} Unable to set playback configuration on NativePlayer
     * @returns {Promise<Void>}
     *
     */
    attachSilkDrm() {

        const errorMessage = {
            method: 'CafPlayerAdapter.attachSilkDrm()',
            get playbackConfiguration() {
                return `${this.method} unable to set playback configuration on NativePlayer`;
            }
        };

        const { nativePlayer, accessToken } = this;

        try {

            /**
             *
             * @type {cast.framework.PlaybackConfig}
             * @desc A copy of the current player config
             *
             */
            const playbackConfig = this.getPlaybackConfig();

            // override license request handler to set credentials
            playbackConfig.licenseRequestHandler = (requestInfo) => {
                requestInfo.withCredentials = true;
                requestInfo.headers = {};
                requestInfo.headers.Authorization = accessToken;
            };

            // set current player config
            nativePlayer.setPlaybackConfig(playbackConfig);

            return Promise.resolve();

        } catch (ex) {

            const reasons = [new ErrorReason('', `${errorMessage.playbackConfiguration} - ${ex}`)];
            const exceptionData = ExceptionReference.common.invalidState;

            return Promise.reject(new ServiceException({ reasons, exceptionData }));

        }

    }

    /**
     *
     * @access private
     * @param {Array<String>} audioSegmentTypes
     * @since 3.2.0
     * @returns {this}
     *
     */
    updateAudioSegmentFormat(audioSegmentTypes) {

        const { framework } = this;

        this.audioSegmentFormat = undefined;

        if (Check.nonEmptyArray(audioSegmentTypes)) {

            if (audioSegmentTypes.includes('FMP4')) {
                this.audioSegmentFormat = Util.getSafe(() => framework.messages.HlsSegmentFormat.FMP4);
                return this;
            }

            if (audioSegmentTypes.includes('PACKED_AUDIO')) {
                this.audioSegmentFormat = Util.getSafe(() => framework.messages.HlsSegmentFormat.TS_AAC);
                return this;
            }

        }

        return this;

    }

    /**
     *
     * @access private
     * @param {Array<String>} videoSegmentTypes
     * @since 3.2.0
     * @returns {this}
     *
     */
    updateVideoSegmentFormat(videoSegmentTypes) {

        const { framework } = this;

        this.videoSegmentFormat = undefined;

        if (Check.nonEmptyArray(videoSegmentTypes)) {

            if (videoSegmentTypes.includes('FMP4')) {
                this.videoSegmentFormat = Util.getSafe(() => framework.messages.HlsVideoSegmentFormat.FMP4);
                return this;
            }

            if (videoSegmentTypes.includes('TS')) {
                this.videoSegmentFormat = Util.getSafe(() => framework.messages.HlsVideoSegmentFormat.MPEG2_TS);
                return this;
            }

        }

        return this;

    }

    /**
     *
     * @access private
     * @since 3.2.0
     * @returns {cast.framework.PlaybackConfig}
     *
     */
    getPlaybackConfig() {

        const { nativePlayer, framework } = this;

        return Object.assign(
            Check.function(nativePlayer.getPlaybackConfig) ? nativePlayer.getPlaybackConfig() || {} : {},
            new framework.PlaybackConfig()
        );

    }

}



// WEBPACK FOOTER //
// ./src/media/playerAdapter/cafPlayerAdapter.js